import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Building2, Plus, TrendingUp, Users, DollarSign, Crown, Trash2, Trophy } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n || 0);

export default function Corporativo() {
  const [dashboard, setDashboard] = useState(null);
  const [sucursales, setSucursales] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [creando, setCreando] = useState(false);
  const [form, setForm] = useState({ nombre_sucursal: '', direccion: '', telefono: '',
                                     nombre_gerente: '', email_gerente: '', password_gerente: '' });
  const [error, setError] = useState('');
  const [prorateo, setProrateo] = useState(null);

  const cargar = () => {
    setCargando(true);
    Promise.all([
      api.get('/corporativo/dashboard').then(r => r.data),
      api.get('/corporativo/sucursales').then(r => r.data),
    ]).then(([d, s]) => { setDashboard(d); setSucursales(s.sucursales); })
      .catch(e => setError(e.response?.data?.detail || 'Error al cargar'))
      .finally(() => setCargando(false));
  };

  useEffect(cargar, []);

  const abrirDialog = async () => {
    setDialog(true);
    setProrateo(null);
    try {
      const r = await api.get('/corporativo/prorateo-preview');
      setProrateo(r.data);
    } catch { /* silent */ }
  };

  const crearSucursal = async () => {
    setCreando(true);
    try {
      await api.post('/corporativo/sucursales', form);
      toast.success('Sucursal creada exitosamente');
      setDialog(false);
      setForm({ nombre_sucursal: '', direccion: '', telefono: '',
                nombre_gerente: '', email_gerente: '', password_gerente: '' });
      cargar();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al crear sucursal'); }
    finally { setCreando(false); }
  };

  const eliminarSucursal = async (id, nombre) => {
    if (!confirm(`¿Eliminar la sucursal "${nombre}"? Los datos se conservan pero la sucursal queda suspendida.`)) return;
    try {
      await api.delete(`/corporativo/sucursales/${id}`);
      toast.success('Sucursal eliminada');
      cargar();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al eliminar'); }
  };

  if (cargando) return <div className="min-h-screen flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
  </div>;

  if (error) return (
    <div className="p-8 max-w-4xl mx-auto">
      <Card className="bg-slate-900/40 border-red-800">
        <CardContent className="p-8 text-center">
          <Crown className="w-12 h-12 text-amber-400 mx-auto mb-3" />
          <h2 className="text-xl font-bold text-slate-100 font-barlow uppercase">{error}</h2>
          <p className="text-sm text-slate-400 mt-2 mb-4">
            El dashboard corporativo requiere el plan Multisucursal. Actualiza tu plan para gestionar múltiples locales.
          </p>
          <a href="/planes"><Button className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold">Ver planes</Button></a>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="corporativo-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-50 font-barlow uppercase tracking-tight flex items-center gap-2">
            <Building2 className="w-8 h-8 text-amber-400" /> Dashboard Corporativo
          </h1>
          <p className="text-sm text-slate-500 mt-1">{dashboard?.mes_actual} · {dashboard?.totales.sucursales} sucursal(es)</p>
        </div>
        <Button onClick={abrirDialog} className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold" data-testid="nueva-sucursal-btn">
          <Plus className="w-4 h-4 mr-2" /> Nueva sucursal
        </Button>
      </div>

      {/* KPIs consolidados */}
      <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
        <KPI icon={DollarSign} label="Ingresos del mes" value={fmt(dashboard?.totales.ingresos_mes)} color="emerald" testid="kpi-ingresos" />
        <KPI icon={TrendingUp} label="Operaciones" value={dashboard?.totales.operaciones_mes || 0} color="cyan" testid="kpi-operaciones" />
        <KPI icon={Users} label="Empleados totales" value={dashboard?.totales.empleados || 0} color="amber" testid="kpi-empleados" />
        <KPI icon={Building2} label="Sucursales" value={dashboard?.totales.sucursales || 0} color="rose" testid="kpi-sucursales" />
      </div>

      {/* Top sucursal */}
      {dashboard?.top_sucursal && dashboard.totales.sucursales > 1 && (
        <Card className="bg-gradient-to-br from-amber-500/10 to-slate-900/40 border-amber-800">
          <CardContent className="p-5 flex items-center gap-4">
            <Trophy className="w-10 h-10 text-amber-400 shrink-0" />
            <div>
              <p className="text-[10px] uppercase tracking-wider text-amber-400">Sucursal top del mes</p>
              <p className="text-xl font-bold text-slate-50 font-barlow">{dashboard.top_sucursal.nombre}</p>
              <p className="text-xs text-slate-400">
                {fmt(dashboard.top_sucursal.ingresos_mes)} · {dashboard.top_sucursal.operaciones_mes} operaciones
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ranking de sucursales */}
      <Card className="bg-slate-900/40 border-slate-800">
        <CardHeader className="pb-2">
          <CardTitle className="font-barlow text-lg text-slate-200">Comparativa por sucursal</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2" data-testid="ranking-sucursales">
            {dashboard?.sucursales.map((s, idx) => {
              const maxIngresos = Math.max(...(dashboard.sucursales || []).map(x => x.ingresos_mes)) || 1;
              const pct = Math.round((s.ingresos_mes / maxIngresos) * 100);
              return (
                <div key={s.id} className="p-3 rounded-sm border border-slate-800 bg-slate-950/40">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500 font-mono text-xs w-6">#{idx + 1}</span>
                      <span className="text-sm font-medium text-slate-100">{s.nombre}</span>
                      {s.es_corporativo && <Badge className="bg-amber-500/20 text-amber-400 border-amber-800 text-[9px]">Cuenta corporativa</Badge>}
                      {s.estado === 'suspendido' && <Badge className="bg-red-500/20 text-red-400 border-red-800 text-[9px]">Suspendida</Badge>}
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-mono text-slate-100 font-bold">{fmt(s.ingresos_mes)}</p>
                      <p className="text-[10px] text-slate-500">{s.operaciones_mes} ops · {s.empleados} emp.</p>
                    </div>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-cyan-500 to-emerald-500" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Lista de sucursales para administrar */}
      <Card className="bg-slate-900/40 border-slate-800">
        <CardHeader className="pb-2">
          <CardTitle className="font-barlow text-lg text-slate-200">Mis Sucursales</CardTitle>
        </CardHeader>
        <CardContent>
          {sucursales.length === 0 ? (
            <div className="text-center py-8">
              <Building2 className="w-12 h-12 text-slate-700 mx-auto mb-2" />
              <p className="text-sm text-slate-500">Aun no tienes sucursales adicionales.</p>
              <p className="text-xs text-slate-600 mt-1">Haz clic en &quot;Nueva sucursal&quot; para agregar tu primera.</p>
            </div>
          ) : (
            <div className="grid gap-2 md:grid-cols-2" data-testid="sucursales-list">
              {sucursales.map(s => (
                <div key={s.id} className={`p-3 rounded-sm border ${s.estado === 'suspendido' ? 'border-red-900/40 bg-red-950/20' : 'border-slate-800 bg-slate-950/40'}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-bold text-slate-100 truncate">{s.nombre}</p>
                      {s.direccion && <p className="text-[11px] text-slate-500 truncate">{s.direccion}</p>}
                      {s.telefono && <p className="text-[11px] text-slate-500">{s.telefono}</p>}
                      <div className="mt-1.5 flex items-center gap-3 text-[11px]">
                        <span className="text-emerald-400 font-mono font-bold">{fmt(s.ingresos_mes)}</span>
                        <span className="text-slate-500">·</span>
                        <span className="text-slate-400">{s.total_usuarios} empleado(s)</span>
                      </div>
                    </div>
                    {s.estado !== 'suspendido' && (
                      <button onClick={() => eliminarSucursal(s.id, s.nombre)}
                        className="text-slate-500 hover:text-red-400 shrink-0"
                        data-testid={`eliminar-sucursal-${s.id}-btn`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog nueva sucursal */}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg" data-testid="dialog-nueva-sucursal">
          <DialogHeader>
            <DialogTitle className="font-barlow text-slate-100">Nueva sucursal</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              Al crear una sucursal se genera una cuenta independiente con su propio gerente. Cada sucursal maneja
              sus propios clientes, staff, órdenes e inventario. Todo lo consolidamos aquí.
            </p>
            {prorateo && (
              <div className="bg-cyan-500/10 border border-cyan-800 rounded-sm p-3" data-testid="prorateo-preview">
                <p className="text-[10px] uppercase tracking-wider text-cyan-400 mb-1">Cargo prorrateado hoy</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-slate-50 font-mono">{fmt(prorateo.monto_prorrateado)}</span>
                  <span className="text-xs text-slate-500">
                    ({prorateo.dias_prorrateados} días · {fmt(prorateo.precio_por_sucursal_mensual)}/mes)
                  </span>
                </div>
                {prorateo.sincroniza_con_corporativo_en && (
                  <p className="text-[11px] text-slate-400 mt-1">
                    Se sincroniza con tu cuenta corporativa el{' '}
                    <span className="text-cyan-300 font-medium">
                      {new Date(prorateo.sincroniza_con_corporativo_en).toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })}
                    </span>. A partir de ahí, ambos cobros salen el mismo día.
                  </p>
                )}
              </div>
            )}
            <div>
              <Label className="text-xs text-slate-400">Nombre de la sucursal *</Label>
              <Input value={form.nombre_sucursal}
                onChange={e => setForm(f => ({ ...f, nombre_sucursal: e.target.value }))}
                placeholder="Ej: TallerPro Centro" className="bg-slate-950 border-slate-700 text-slate-200"
                data-testid="input-nombre-sucursal" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-400">Dirección</Label>
                <Input value={form.direccion}
                  onChange={e => setForm(f => ({ ...f, direccion: e.target.value }))}
                  placeholder="Av. Reforma 123" className="bg-slate-950 border-slate-700 text-slate-200" />
              </div>
              <div>
                <Label className="text-xs text-slate-400">Teléfono</Label>
                <Input value={form.telefono}
                  onChange={e => setForm(f => ({ ...f, telefono: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                  placeholder="10 dígitos" className="bg-slate-950 border-slate-700 text-slate-200 font-mono" />
              </div>
            </div>
            <div className="pt-2 border-t border-slate-800">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Cuenta del gerente de sucursal</p>
              <div className="space-y-2">
                <div>
                  <Label className="text-xs text-slate-400">Nombre del gerente *</Label>
                  <Input value={form.nombre_gerente}
                    onChange={e => setForm(f => ({ ...f, nombre_gerente: e.target.value }))}
                    className="bg-slate-950 border-slate-700 text-slate-200"
                    data-testid="input-nombre-gerente" />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Email del gerente *</Label>
                  <Input type="email" value={form.email_gerente}
                    onChange={e => setForm(f => ({ ...f, email_gerente: e.target.value.toLowerCase() }))}
                    className="bg-slate-950 border-slate-700 text-slate-200 font-mono"
                    data-testid="input-email-gerente" />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Contraseña inicial * (mínimo 6 caracteres)</Label>
                  <Input type="text" value={form.password_gerente}
                    onChange={e => setForm(f => ({ ...f, password_gerente: e.target.value }))}
                    className="bg-slate-950 border-slate-700 text-slate-200 font-mono"
                    data-testid="input-password-gerente" />
                  <p className="text-[10px] text-slate-500 mt-1">El gerente podrá cambiarla luego desde su perfil.</p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(false)} className="text-slate-400">Cancelar</Button>
            <Button onClick={crearSucursal} disabled={creando}
              className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold" data-testid="confirmar-crear-sucursal-btn">
              {creando ? 'Creando...' : 'Crear sucursal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KPI({ icon: Icon, label, value, color, testid }) {
  const colors = {
    emerald: 'text-emerald-400 bg-emerald-500/10',
    cyan: 'text-cyan-400 bg-cyan-500/10',
    amber: 'text-amber-400 bg-amber-500/10',
    rose: 'text-rose-400 bg-rose-500/10',
  };
  return (
    <Card className="bg-slate-900/40 border-slate-800" data-testid={testid}>
      <CardContent className="p-4">
        <div className={`w-9 h-9 rounded-sm ${colors[color]} flex items-center justify-center mb-2`}>
          <Icon className="w-4 h-4" />
        </div>
        <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
        <p className="text-xl font-bold text-slate-50 font-barlow font-mono">{value}</p>
      </CardContent>
    </Card>
  );
}
